<script setup lang="ts">
import Loader from '@/components/ui/Loader.vue';

interface Props {
  type?: 'primary' | 'secondary' | 'danger' | 'singleIcon' | 'outline';
  padding?: string;
  disabled?: boolean;
  loading?: boolean;
  width?: string;
  small?: boolean;
  editor?: boolean;
}

withDefaults(defineProps<Props>(), {
  type: 'primary',
  padding: 'md',
  disabled: false,
  loading: false,
  editor: false,
  width: 'auto'
});
</script>

<template>
  <button
    :class="[`button-base--${padding}`, `button-base--${type}`, { 'small': small, 'editor': editor }]"
    :disabled="disabled"
    :style="`width: ${width}`"
    class="button-base"
  >
    <Loader
      v-if="loading"
      class="loading-spinner"
    />
    <slot v-else />
  </button>
</template>

<style lang="scss" scoped>
.button-base {
  display: inline-flex;
  align-items: center;
  border: solid 1px var(--blue);
  border-radius: 2px;
  background: var(--blue);
  font-size: 14px;
  font-weight: 700;
  line-height: 0.91;
  color: var(--white);
  height: 40px;
  justify-content: center;
  text-transform: uppercase;
  cursor: pointer;
  gap: 5px;
  min-width: 80px;

  &:hover,
  &:focus,
  &:active {
    background: var(--blue-darker);
  }

  &:active {
    transform: translateY(1px);
  }

  &.small {
    padding: 5px 14px;
    font-size: 12px;
    height: 32px;
    margin-right: 10px;
  }

  &.editor {
    padding: 0;
  }

  .loading-spinner {
    width: 20px;
    height: 20px;

    &:deep(path) {
      fill: var(--white)!important;
    }
  }

  &--secondary {
    border: solid 1px var(--border-default);
    background-color: var(--white);
    color: var(--text-color);

    &:hover,
    &:focus,
    &:active {
      background: var(--gray);
    }

    .loading-spinner {
      &:deep(path) {
        fill: var(--primary)!important;
      }
    }
  }

  &--danger {
    border: solid 1px var(--red);
    background: var(--red);
    color: var(--white);

    &:hover,
    &:focus,
    &:active {
      background: var(--red);
      color: var(--white);
    }
  }

  &--sm {
    padding: 13px 15px;
  }

  &--md {
    padding: 13px 20px;
  }

  &--lg {
    padding: 9px 16px;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &--singleIcon {
    background-color: transparent;
    border: none;
    border-radius: 2px;
    padding: 5px;
    height: 32px;
    min-width: auto;

    &:hover,
    &:focus,
    &:active {
      background: transparent;
    }
  }

  &--outline {
    border: none;
    color: var(--blue);
    background: none;
    padding: 0;
    height: auto;
    text-transform: unset;

    &:hover,
    &:focus,
    &:active {
      background: transparent;
      color: var(--blue-darker);
    }
  }
}
</style>
